var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { memo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import useKeyboard from '../../../core/hooks/useKeyboard';
import Menu, { MenuItem } from '../../../core/components/Menu';
import Dropdown from '../../../core/components/Dropdown';
import { ChevronDown } from '../../../icons';
import { DefaultTrigger, ItemsContainer } from './styles';
const itemRenderer = ({ value }) => React.createElement("div", null, value);
const triggerRenderer = (_a) => {
    var { placeholder, selected } = _a, props = __rest(_a, ["placeholder", "selected"]);
    return (React.createElement(DefaultTrigger, Object.assign({}, props),
        selected.length ? (React.createElement(ItemsContainer, null, selected.map(({ name, value }) => (React.createElement("span", { key: value }, name))))) : (React.createElement("div", null, placeholder)),
        React.createElement(ChevronDown, { height: 14, width: 14 })));
};
const Select = ({ value = [], onSelect = () => { }, options = [], multiple, disabled, parentContainer = null, renderItem = itemRenderer, renderTrigger = triggerRenderer, 
// we pass isOpen and handleClose to manage dropdown state from parent
isOpen, handleClose, placeholder = 'Select...', className = '', 'data-qa-anchor': dataQaAnchor = '', }) => {
    const [isOpenInternal, setIsOpenInternal] = useState(isOpen);
    const [selected, setSelected] = useState(value);
    const toggle = () => setIsOpenInternal(!isOpenInternal);
    const close = () => (handleClose ? handleClose() : setIsOpenInternal(false));
    const remove = (currentItem, callback) => {
        setSelected((prev) => {
            const newSelected = prev.filter((item) => item.value !== currentItem);
            callback && callback(newSelected.map((item) => item.value));
            return newSelected;
        });
    };
    useKeyboard({
        Escape: close,
    });
    // sync internal state
    useEffect(() => {
        setIsOpenInternal(isOpen);
    }, [isOpen]);
    const handleSelect = (selectedItem) => {
        onSelect(selectedItem);
        if (multiple) {
            const index = selected.findIndex((item) => item.value === selectedItem.value);
            if (index >= 0) {
                // remove item if selected twice
                remove(index);
            }
            else {
                setSelected([...selected, selectedItem]);
            }
        }
        else {
            setSelected([selectedItem]);
            close();
        }
    };
    const handleClick = (e) => {
        e.preventDefault();
        toggle();
    };
    return (React.createElement(Dropdown, { "data-qa-anchor": `${dataQaAnchor}-select-dropdown`, isOpen: isOpen || isOpenInternal, renderTrigger: (props) => renderTrigger(Object.assign(Object.assign({}, props), { onClick: handleClick, selected, remove, placeholder })), 
        // when using custom trigger we should handle "close on click outside" (if needed)
        handleClose: close, fullSized: true, scrollable: true, parentContainer: parentContainer, disabled: disabled, className: className }, options && options.length > 0 && (React.createElement(Menu, null, options.map((option) => {
        return (React.createElement(MenuItem, { key: option.value, "data-qa-anchor": `${dataQaAnchor}-select-menu-item`, active: selected.find((item) => item.value === option.value), onClick: () => handleSelect(option) }, renderItem(option)));
    })))));
};
Select.propTypes = {
    'data-qa-anchor': PropTypes.string,
    value: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, value: PropTypes.any })),
    options: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, value: PropTypes.any })),
    multiple: PropTypes.bool,
    disabled: PropTypes.bool,
    parentContainer: PropTypes.instanceOf(Element),
    renderItem: PropTypes.func,
    renderTrigger: PropTypes.func,
    isOpen: PropTypes.bool,
    handleClose: PropTypes.func,
    placeholder: PropTypes.string,
    className: PropTypes.string,
    onSelect: PropTypes.func,
};
export default memo(Select);
//# sourceMappingURL=index.js.map